/* eslint-disable import/exports-last */
import User from "../users/User";

import Barrel from "./Barrel";

import ProjectActivityEvent, {
    ProjectActivityEventCaughtUp,
    ProjectActivityEventNoActivities,
    ProjectActivityEventProjectCreated,
    ProjectActivityEventProjectUserJoined,
    ProjectActivityEventUserCreateScreen,
    ProjectActivityEventUserCreateScreenCommentMention,
    ProjectActivityEventUserCreateScreenVersion,
    ProjectActivityEventUserUpdateScreenStatusTag
} from "./ProjectActivityEvent";
import ProjectActivityTagUpdate from "./ProjectActivityTag";
import ProjectActivityType, { isScreenActivityType } from "./ProjectActivityType";

interface ProjectActivityBase {
    _id: string;
    type: ProjectActivityType;
    created: string;
    updated: string;
    actor: User;
    incomplete: boolean;
    events: ProjectActivityEvent[];
}

export interface ProjectActivityNoActivities extends ProjectActivityBase {
    type: ProjectActivityType.NoActivities;
    events: ProjectActivityEventNoActivities[];
}

export interface ProjectActivityCaughtUp extends ProjectActivityBase {
    type: ProjectActivityType.CaughtUp;
    events: ProjectActivityEventCaughtUp[];
}

export interface ProjectActivityProjectCreated extends ProjectActivityBase {
    type: ProjectActivityType.ProjectCreated;
    events: ProjectActivityEventProjectCreated[];
}

export interface ProjectActivityProjectUserJoined extends ProjectActivityBase {
    type: ProjectActivityType.ProjectUserJoined;
    events: ProjectActivityEventProjectUserJoined[];
}

export interface ProjectActivityUserCreateScreen extends ProjectActivityBase {
    type: ProjectActivityType.UserCreateScreen;
    events: ProjectActivityEventUserCreateScreen[];
}

export interface ProjectActivityUserCreateScreenVersion extends ProjectActivityBase {
    type: ProjectActivityType.UserCreateScreenVersion;
    events: ProjectActivityEventUserCreateScreenVersion[];
}

export interface ProjectActivityUserCreateScreenCommentMention extends ProjectActivityBase {
    type: ProjectActivityType.UserCreateScreenCommentMention;
    events: ProjectActivityEventUserCreateScreenCommentMention[];
}

export interface ProjectActivityUserUpdateScreenStatusTag extends ProjectActivityBase {
    type: ProjectActivityType.UserUpdateScreenStatusTag;
    tag: ProjectActivityTagUpdate;
    events: ProjectActivityEventUserUpdateScreenStatusTag[];
}

export type NonInteractiveProjectActivity =
    ProjectActivityNoActivities |
    ProjectActivityCaughtUp |
    ProjectActivityProjectCreated |
    ProjectActivityProjectUserJoined;

export type ScreenActivity =
    ProjectActivityUserCreateScreen |
    ProjectActivityUserCreateScreenVersion;

export type AuthoredActivity =
    ProjectActivityUserCreateScreen |
    ProjectActivityUserCreateScreenVersion |
    ProjectActivityUserUpdateScreenStatusTag;

export type TaggableActivity =
    ProjectActivityUserCreateScreen |
    ProjectActivityUserCreateScreenVersion |
    ProjectActivityUserUpdateScreenStatusTag;

export type InteractiveProjectActivity =
    ProjectActivityUserCreateScreen |
    ProjectActivityUserCreateScreenVersion |
    ProjectActivityUserUpdateScreenStatusTag |
    ProjectActivityUserCreateScreenCommentMention;

type ProjectActivity = NonInteractiveProjectActivity | InteractiveProjectActivity;

export function createNoActivitiesActivity(
    barrel: Pick<Barrel, "_id">
): ProjectActivityNoActivities {
    const now = new Date().toISOString();

    return {
        _id: `${barrel._id}-no-activities-activity`,
        type: ProjectActivityType.NoActivities,
        created: now,
        updated: now,
        actor: {
            _id: barrel._id
        },
        incomplete: true,
        events: [{
            _id: `${barrel._id}-no-activities-activity-event`,
            type: ProjectActivityType.NoActivities,
            created: now,
            updated: now
        }]
    };
}

export function createCaughtUpActivity(
    barrel: Pick<Barrel, "_id">
): ProjectActivityCaughtUp {
    const now = new Date().toISOString();

    return {
        _id: `${barrel._id}-caught-up-activity`,
        type: ProjectActivityType.CaughtUp,
        created: now,
        updated: now,
        actor: {
            _id: barrel._id
        },
        incomplete: false,
        events: [{
            _id: `${barrel._id}-caught-up-activity-event`,
            type: ProjectActivityType.CaughtUp,
            created: now,
            updated: now
        }]
    };
}

export function createProjectCreatedActivity(
    barrel: Pick<Barrel, "_id" | "created">
): ProjectActivityProjectCreated {
    return {
        _id: `${barrel._id}-project-created-activity`,
        type: ProjectActivityType.ProjectCreated,
        created: barrel.created,
        updated: barrel.created,
        actor: {
            _id: barrel._id
        },
        incomplete: false,
        events: [{
            _id: `${barrel._id}-project-created-activity-event`,
            type: ProjectActivityType.ProjectCreated,
            created: barrel.created,
            updated: barrel.created
        }]
    };
}

export function isScreenActivity(
    activity: ProjectActivity
): activity is ProjectActivityUserCreateScreen | ProjectActivityUserCreateScreenVersion {
    return isScreenActivityType(activity.type);
}

export default ProjectActivity;
